import {
    Col,
    Row,
    Space,
    Typography,
    Card,
    Checkbox,
    Button,
    Tag,
    Image,
    theme
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState, useMemo, useRef } from "react";
import { useGetQuery } from "../../service/query/Queries";
import {TestResultModal} from "../../components/";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./Test.css";
const { Title } = Typography;

// Pause before the test jumps to the next question, so the answer colour
// is still on screen when the answer is locked in.
const AUTO_NEXT_DELAY = 700;

// State driving exam rules for random ticket modes: question count -> max wrong answers allowed.
const EXAM_MAX_WRONG = { 20: 2, 50: 4 };

const Test = () => {
    const [oneTest, setOneTest] = useState(null);
    const [results, setResults] = useState({});
    const location = useLocation();
    const { pathname } = location;
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get("editId");
    const count = queryParams.get("count");
    const { control, reset } = useForm({ defaultValues: {} });
    const { t, i18n } = useTranslation();
    const [time, setTime] = useState(0);
    const [finished, setFinished] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [stats, setStats] = useState({
        correct: 0,
        wrong: 0,
        skipped: 0,
        total: 0
    });
    const { token } = theme.useToken();
    const { doubleClickConfirm } = useSelector((state) => state.settings);
    // Pending auto-advance, kept in a ref so navigation can cancel it.
    const autoNextTimer = useRef(null);

    useEffect(() => () => clearTimeout(autoNextTimer.current), []);

    const { data: getThemeTest, refetch: refetchGetThemeTest } =
        useGetQuery(false, "get-test-theme", `/theme/themes/${editId}`, false);

    const { data: getTicketTest, refetch: refetchGetTicketTest } =
        useGetQuery(false, "get-test-ticket", `/ticket/themes/${editId}`, false);

    const { data: getExamTest, refetch: refetchGetExamTest } =
        useGetQuery(false, "get-test-exam", `/theme/exam-questions/?start=1${count ? `&count=${count}` : ""}`, false);
    const { data: getTicketTopicName, refetch: refetchGetTicketTopicName } =
        useGetQuery(false, "get-topic-name", `ticket/theme/${editId}/`, false);
    const { data: getThemeTopicName, refetch: refetchGetThemeTopicName } =
        useGetQuery(false, "get-theme-name", `/theme/theme/${editId}/`, false);
    const { data: getExamTopicName, refetch: refetchGetExamTopicName } =
        useGetQuery(false, "get-exam-name", `/theme/exam/${editId}/`, false);

    const getTest = useMemo(() => {
        if (pathname === "/theme/test") return getThemeTest || [];
        if (pathname === "/ticket/test") return getTicketTest || [];
        if (pathname === "/random/test") return getExamTest || [];
        return [];
    }, [pathname, getThemeTest, getTicketTest, getExamTest]);

    const getTopicName =useMemo( ()=> {
        if (pathname === "/theme/test") return getThemeTopicName?.name || '';
        if (pathname === "/ticket/test") return getTicketTopicName?.name || '';
        if (pathname === "/random/test") {
            if (!editId && count) return t(`random_${count}`);
            return getExamTopicName?.name || '';
        }
        return '';
    }, [ getTicketTopicName ,getThemeTopicName , getExamTopicName, editId, count, t, pathname])

    useEffect(() => {
        if (pathname === "/theme/test") {
            refetchGetThemeTopicName();
            refetchGetThemeTest();
        }
        if (pathname === "/ticket/test") {
            refetchGetTicketTest();
            refetchGetTicketTopicName()
        }
        if (pathname === "/random/test") {
            refetchGetExamTest();
            if (editId) refetchGetExamTopicName();
        }
    }, [pathname, editId, i18n.language]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                if (pathname === "/theme/test") {
                    await refetchGetThemeTopicName();
                }
                if (pathname === "/ticket/test") {
                    await refetchGetTicketTopicName()
                }
                if (pathname === "/random/test" && editId) {
                    await refetchGetExamTopicName();
                }
            } catch (err) {
                localStorage.removeItem("testResults");
                localStorage.removeItem("currentQuestion");
                localStorage.removeItem("testTimer");
                navigate("/login");
            }
        }, 10000);

        return () => clearInterval(interval);
    }, [ navigate]);


    useEffect(() => {
        if (getTest?.length > 0) {
            if (oneTest) {
                const current = getTest?.find((q) => q.id === oneTest.id);
                if (current) {
                    setOneTest(current);
                    return;
                }
            }
            setOneTest(getTest[0]);
        }
    }, [getTest, oneTest?.id]);

    useEffect(() => {
        const saved = localStorage.getItem("testResults");
        if (saved) setResults(JSON.parse(saved));
        const savedQuestion = localStorage.getItem("currentQuestion");
        if (savedQuestion) setOneTest(JSON.parse(savedQuestion));
        const savedTime = localStorage.getItem("testTimer");
        if (savedTime) setTime(parseInt(savedTime, 10));
    }, []);

    useEffect(() => {
        if (finished) return;
        const interval = setInterval(() => {
            setTime((prev) => {
                const newTime = prev + 1;
                localStorage.setItem("testTimer", newTime);
                return newTime;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [finished]);

    useEffect(() => {
        if (oneTest) {
            localStorage.setItem("currentQuestion", JSON.stringify(oneTest));
        }
    }, [oneTest]);

    useEffect(() => {
        if (oneTest?.answers) {
            const prev = results[oneTest.id]?.selected || {};
            reset(
                oneTest.answers.reduce((acc, opt) => {
                    acc[opt.id] = prev[opt.id] ?? false;
                    return acc;
                }, {})
            );
        }
    }, [oneTest, reset, results]);

    // Move on by itself once an answer is locked in. The last question stays
    // put so the user can still press "finish".
    const goToNextQuestion = () => {
        const currentIndex = getTest?.findIndex((q) => q.id === oneTest?.id);
        if (currentIndex === -1 || currentIndex >= getTest?.length - 1) return;
        const next = getTest[currentIndex + 1];
        clearTimeout(autoNextTimer.current);
        autoNextTimer.current = setTimeout(
            () => setOneTest(next),
            AUTO_NEXT_DELAY
        );
    };

    const handleAnswerChange = (opt, checked) => {
        const prevTest = results[oneTest.id] || {
            selected: {},
            firstAnswer: null,
            pending: null
        };
        let selected = prevTest.selected;
        let firstAnswer = prevTest.firstAnswer;
        let pending = prevTest.pending || null;

        // Two-click mode: the answer is only scored after the same option is
        // clicked a second time. Once confirmed the question behaves as before.
        // `checked` is ignored here — the second click on a marked option
        // arrives as an uncheck, but it still means "confirm".
        if (doubleClickConfirm && !firstAnswer) {
            const confirmed = pending?.id === opt.id;
            // Only one option can be marked at a time, so drop the others.
            selected = { [opt.id]: true };
            firstAnswer = confirmed ? opt : null;
            pending = confirmed ? null : opt;
        } else {
            selected = { ...selected, [opt.id]: checked };
            if (!firstAnswer && checked) {
                firstAnswer = opt;
            }
        }

        const updated = {
            ...results,
            [oneTest.id]: { selected, firstAnswer, pending }
        };
        localStorage.setItem("testResults", JSON.stringify(updated));
        setResults(updated);

        // Only the click that actually locks the answer advances: in two-click
        // mode that is the confirming click, and clicking around an already
        // answered question stays where it is.
        if (!prevTest.firstAnswer && firstAnswer) {
            goToNextQuestion();
        }
    };

    // Answer-state palette shared by the option rows and the question chips at
    // the bottom, so the same state reads the same in both places.
    const answerColors = useMemo(
        () => ({
            idle: token.colorFillTertiary,
            pending: token.responsibleTask,
            correct: token.activeDay,
            wrong: "#fa4f4f"
        }),
        [token]
    );

    const examMaxWrong =
        pathname === "/random/test" ? EXAM_MAX_WRONG[Number(count)] : undefined;
    const isExamMode = examMaxWrong !== undefined;

    // Live exam verdict: "failed" once the wrong limit is exceeded, "passed"
    // once enough answers are correct that the rest cannot fail it.
    const examStatus = useMemo(() => {
        if (!isExamMode) return null;
        let correct = 0,
            wrong = 0;
        getTest.forEach((q) => {
            const first = results[q.id]?.firstAnswer;
            if (!first) return;
            if (first.is_correct) correct++;
            else wrong++;
        });
        if (wrong > examMaxWrong) return "failed";
        if (
            getTest.length > 0 &&
            correct >= getTest.length - examMaxWrong
        ) {
            return "passed";
        }
        return null;
    }, [isExamMode, examMaxWrong, getTest, results]);

    const examColor =
        examStatus === "failed"
            ? answerColors.wrong
            : examStatus === "passed"
            ? answerColors.correct
            : null;

    const getColor = (opt) => {
        const res = results[oneTest?.id];
        if (!res) return answerColors.idle;
        if (res.pending?.id === opt.id) {
            return answerColors.pending;
        }
        if (res.firstAnswer?.id === opt.id) {
            return opt.is_correct ? answerColors.correct : answerColors.wrong;
        }
        if (res.selected?.[opt.id]) {
            return opt.is_correct ? answerColors.correct : answerColors.wrong;
        }
        return answerColors.idle;
    };

    const handleSelectOneTest = (test) => {
        // Manual navigation wins over a queued auto-advance.
        clearTimeout(autoNextTimer.current);
        setOneTest(test);
    };

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s < 10 ? "0" + s : s}`;
    };

    const handleFinish = () => {
        setFinished(true);
        let correct = 0,
            wrong = 0,
            skipped = 0;
        getTest?.forEach((q) => {
            const res = results[q.id];
            if (!res?.firstAnswer) {
                skipped++;
            } else if (res.firstAnswer.is_correct) {
                correct++;
            } else {
                wrong++;
            }
        });
        setStats({
            correct,
            wrong,
            skipped,
            total: getTest?.length
        });
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        localStorage.removeItem("testResults");
        localStorage.removeItem("currentQuestion");
        localStorage.removeItem("testTimer");

        if (pathname === "/theme/test") {
            navigate("/themes");
        } else if (pathname === "/random/test") {
            navigate("/random");
        } else {
            navigate("/ticket");
        }
    };
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!oneTest?.answers) return;
            if (e.code.startsWith("F")) {
                const index = parseInt(e.code.replace("F", ""), 10) - 1;
                const selectedAnswer = oneTest.answers[index];
                if (selectedAnswer) {
                    // The form is synced from `results` by the effect above.
                    handleAnswerChange(selectedAnswer, true);
                }
                e.preventDefault();
            }
            if (e.code === "ArrowRight") {
                const currentIndex = getTest?.findIndex(q => q.id === oneTest?.id);
                if (currentIndex !== -1 && currentIndex < getTest?.length - 1) {
                    handleSelectOneTest(getTest[currentIndex + 1]);
                }
                e.preventDefault();
            }
            if (e.code === "ArrowLeft") {
                const currentIndex = getTest?.findIndex(q => q.id === oneTest?.id);
                if (currentIndex > 0) {
                    handleSelectOneTest(getTest[currentIndex - 1]);
                }
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [oneTest, results, reset, getTest]);

    return (
        <div className="site-space-compact-wrapper">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Row gutter={[8, 8]}>
                    <Col span={24}>
                        <Card style={{border:'none'}}>
                            <div
                                style={{
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    minHeight: 44
                                }}
                            >
                                <Title
                                    level={5}
                                    style={{ fontWeight: "bold", margin:0  }}
                                >
                                    {getTopicName}
                                </Title>
                                {examStatus && (
                                    <Tag
                                        style={{
                                            backgroundColor: examColor,
                                            borderColor: examColor,
                                            color: "#FFFFFF",
                                            fontWeight: "bold",
                                            // Same size as the question text (Title level 3).
                                            fontSize: token.fontSizeHeading3,
                                            lineHeight: token.lineHeightHeading3,
                                            padding: "4px 16px",
                                            margin: 0,
                                            position: "absolute",
                                            left: "50%",
                                            transform: "translateX(-50%)"
                                        }}
                                    >
                                        {t(`exam_${examStatus}`)}
                                    </Tag>
                                )}
                            </div>
                            <Title
                                className="page--title"
                                level={3}
                                style={{ fontWeight: "bold", borderBottom:`1px solid ${token.activeDay}` , paddingBottom:"10px" }}
                            >
                                {oneTest?.text}
                            </Title>
                        </Card>
                    </Col>

                    <Col xs={24} md={12} style={{margin:"10px 0 "}}>
                            <Space direction="vertical" size={4} style={{ width: "100%" }}>
                                {oneTest?.answers?.map((opt, index) => (
                                    <Controller
                                        key={opt?.id}
                                        name={String(opt.id)}
                                        control={control}
                                        render={({ field }) => (
                                            <Checkbox
                                                checked={field.value || false}
                                                onChange={(e) => {
                                                    field.onChange(e.target.checked);
                                                    handleAnswerChange(opt, e.target.checked);
                                                }}
                                                className="custom-checkbox"
                                                style={{
                                                    borderRadius: "4px",
                                                    border: `1px solid ${token.meetingBorderColor}`,
                                                    backgroundColor: getColor(opt),
                                                    cursor: "pointer",
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "stretch",
                                                    padding: 0,
                                                    margin: 0,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        display: "flex",
                                                        gap: 0,
                                                        alignItems: "stretch",
                                                        padding: 0,
                                                    }}
                                                >
                                                    <h5
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            margin: 0,
                                                            fontWeight: "bold",
                                                            padding: "8px 14px",
                                                            color: "white",
                                                            borderRight: `1px solid ${token.meetingBorderColor}`,
                                                            fontSize: "clamp(15px, 1.6vw, 18px)",
                                                            backgroundColor: token.meetingBorderColor,
                                                            minWidth: 44,
                                                            flexShrink: 0,
                                                            lineHeight: 1,
                                                            alignSelf: "stretch",
                                                        }}
                                                    >
                                                        F{index + 1}
                                                    </h5>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            padding: "8px 14px",
                                                            fontSize: "clamp(15px, 1.6vw, 17px)",
                                                            flex: 1,
                                                            wordBreak: "break-word",
                                                            whiteSpace: "normal",
                                                            lineHeight: 1.3,
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {opt?.text}
                                                    </p>
                                                </div>
                                            </Checkbox>
                                        )}
                                    />
                                ))}
                            </Space>
                    </Col>

                    <Col xs={24} md={12}>
                        {oneTest?.image ? (
                            <Image
                                src={oneTest.image}
                                alt="question"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "400px",
                                    objectFit: "contain",
                                    borderRadius: "6px"
                                }}
                            />
                        ) : (
                            <img
                                src="/avto-shkola-bg.jpg"
                                alt="logo"
                                style={{
                                    width: "100%",
                                    minHeight: "400px",
                                    objectFit: "cover",
                                    borderRadius: "6px"
                                }}
                            />
                        )}
                    </Col>
                </Row>
                <Row gutter={[10, 20]}>
                    <Col xs={24} md={21}>
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                flexWrap: "wrap",
                                // Tighter horizontal gap between the question
                                // chips; rows keep their breathing room.
                                gap: "10px 5px"
                            }}
                        >
                            {getTest?.map((testCount, index) => {
                                const res = results[testCount?.id];
                                const isCurrent = oneTest?.id === testCount?.id;
                                // Untouched questions stay grey; answered ones
                                // keep their result colour.
                                let bg = answerColors.idle;
                                let color = token.colorTextDescription;
                                if (res?.firstAnswer) {
                                    bg = res.firstAnswer.is_correct
                                        ? answerColors.correct
                                        : answerColors.wrong;
                                    color = "#FFFFFF";
                                } else if (res?.pending) {
                                    bg = answerColors.pending;
                                    color = "#FFFFFF";
                                } else if (isCurrent) {
                                    bg = token.meetingBgColor;
                                    color = token.meetingBorderColor;
                                }

                                return (
                                    <button
                                        key={testCount?.id}
                                        onClick={() =>
                                            handleSelectOneTest(testCount)
                                        }
                                        style={{
                                            width: 35,
                                            height: 35,
                                            // The current question keeps a blue
                                            // ring so it stays recognisable on
                                            // top of any answer colour.
                                            border: isCurrent
                                                ? `2px solid ${token.meetingBorderColor}`
                                                : `1px solid ${token.colorBorder}`,
                                            boxShadow: isCurrent
                                                ? `0 0 0 3px ${token.meetingBorderColor}40`
                                                : "none",
                                            transform: isCurrent
                                                ? "scale(1.08)"
                                                : "none",
                                            transition:
                                                "transform 0.15s, box-shadow 0.15s",
                                            backgroundColor: bg,
                                            color,
                                            flexShrink: 0,
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontWeight: isCurrent
                                                ? "bold"
                                                : "normal"
                                        }}
                                    >
                                        {index + 1}
                                    </button>
                                );
                            })}
                        </div>
                    </Col>
                    <Col xs={24} md={3}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px"
                            }}
                        >
                            <Tag
                                color="lime"
                                style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    margin: 0
                                }}
                            >
                                <p style={{ margin: 0, fontSize: 20 }}>
                                    {formatTime(time)}
                                </p>
                            </Tag>
                            <Button
                                type="primary"
                                danger
                                onClick={handleFinish}
                                size={"large"}
                            >
                                {t("finish_test")}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Space>
            <TestResultModal
                questions={getTest}
                results={results}
                open={modalOpen}
                onClose={handleModalClose}
                correct={stats.correct}
                wrong={stats.wrong}
                skipped={stats.skipped}
                total={stats.total}
                time={time}
            />
        </div>
    );
};

export default Test;
